import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { SOFTWAREIQ_DATASETS, SoftwareIqNavigationIcon } from '@softwareiq/brand-ui';
import './ResearchDatasetMenu.css';
import {
  ANALYTICS_NAV_GROUP_IDS,
  buildAnalyticsSidebarGroups,
  getAnalyticsSidebarStateFromPath,
} from '../../configs/analyticsSidebarNavigation';

const DATASETS = ['operating-kpis', 'job-postings', 'developer-signals', 'market-benchmarks'];
const DOCUMENTATION = {
  'operating-kpis': '/documentation/api/operating-kpis',
  'job-postings': '/documentation/api/job-postings',
  'developer-signals': '/documentation/adoption-signals',
};

export default function ResearchDatasetMenu({ onNavigate, mobile = false }) {
  const theme = useTheme();
  const { pathname, search } = useLocation();
  const active = getAnalyticsSidebarStateFromPath(pathname);
  const [expanded, setExpanded] = useState(DATASETS.includes(active.activeNavigationItemId) ? active.activeNavigationItemId : DATASETS[0]);
  const [levelId, setLevelId] = useState(active.activeNavigationLevelId);
  const previewTimer = useRef();
  useEffect(() => () => clearTimeout(previewTimer.current), []);
  const cancelPreview = () => clearTimeout(previewTimer.current);
  const schedulePreview = action => {
    if (mobile) return;
    cancelPreview();
    previewTimer.current = setTimeout(action, 140);
  };
  const params = new URLSearchParams(search);
  const items = buildAnalyticsSidebarGroups({
    activeGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
    activeItemId: active.activeNavigationItemId,
    activeLevelId: active.activeNavigationLevelId,
    filters: Object.fromEntries(['ticker', 'category'].filter(key => params.has(key)).map(key => [key, params.get(key)])),
    projectId: params.get('projectId') || undefined,
  }).flatMap(group => group.items).filter(item => DATASETS.includes(item.id));
  const follow = event => {
    if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) onNavigate?.();
  };
  const selected = items.find(item => item.id === expanded);
  const descriptor = id => SOFTWAREIQ_DATASETS.find(item => item.id === id);
  const showDataset = item => {
    cancelPreview();
    setExpanded(item.id);
    setLevelId(active.activeNavigationItemId === item.id ? active.activeNavigationLevelId : item.levels?.[0]?.id);
  };
  const selectedLevel = selected?.levels?.find(level => level.id === levelId) || selected?.levels?.[0];
  const shortLabel = label => ({
    'KPI Trends': 'Trends', 'Valuation Benchmarks': 'Valuation', 'KPI Distribution': 'Distribution',
    'Financial Overview': 'Overview', 'Current Metrics': 'Metrics', 'Comp Sheet': 'Comps',
    'Posting Trend': 'Trends', 'Active Job Board': 'Job board', 'Daily Hiring': 'Daily',
    'Partner Trends': 'Partners', 'Project Analysis': 'Analysis', 'Index Performance': 'Index',
    'Market Valuation': 'Valuation', 'Market Map': 'Map',
  }[label] || label);
  const depthLabel = label => ({ Vendor: 'Companies', 'Company Detail': 'Company', Project: 'Projects', 'Project Detail': 'Detail' }[label] || label);
  const pageLink = (item, view) => {
    const Icon = view.icon;
    return <Link key={view.id} to={view.to} onClick={follow} aria-label={view.label} title={`${view.label}: ${view.description || ''}`}
      aria-describedby={view.description ? `${item.id}-${view.id}-description` : undefined}
      aria-current={active.activeNavigationItemId === item.id && (active.activeNavigationViewId === view.id || pathname === view.to) ? 'page' : undefined}
      className="research-dataset-page">
      {Icon && <span className="research-dataset-page-icon" aria-hidden="true"><Icon /></span>}
      <span className="research-dataset-page-copy"><strong>{shortLabel(view.label)}</strong>
        {view.description && <small id={`${item.id}-${view.id}-description`}>{view.description}</small>}
      </span>
      <ChevronRightRoundedIcon className="research-dataset-page-indicator" aria-hidden="true" />
    </Link>;
  };
  return <Box component="nav" aria-label="Research datasets"
    className={`siq-dataset-panel research-dataset-menu${mobile ? ' research-dataset-menu--mobile' : ''}${theme.palette.mode === 'dark' ? ' siq-dataset-panel--dark' : ''}`}>
    <div className="siq-dataset-panel__rail research-dataset-rail">
      <p className="siq-dataset-panel__section-label">Datasets</p>
      {items.map(item => {
        const info = descriptor(item.id);
        const open = expanded === item.id;
        return <div key={item.id} className={`research-dataset-row${open ? ' is-selected' : ''}`}
          onMouseEnter={() => schedulePreview(() => showDataset(item))} onMouseLeave={cancelPreview}>
          <button type="button" className="research-dataset-preview" aria-label={`Preview ${item.label} pages`}
            aria-pressed={open} aria-controls="research-dataset-detail" onClick={() => showDataset(item)}
            onFocus={() => { if (!mobile) showDataset(item); }}>
            <SoftwareIqNavigationIcon name={info.icon} />
          </button>
          <Link to={item.defaultTo} onClick={follow} className="research-dataset-link" aria-label={item.label}
            onFocus={() => { if (!mobile) showDataset(item); }}
            aria-current={active.activeNavigationItemId === item.id ? 'page' : undefined}>
            <span><strong>{item.label}</strong><small>{info.eyebrow}</small></span>
          </Link>
        </div>;
      })}
      <p className="research-dataset-hint">{mobile ? 'Tap an icon to preview, or a dataset name to open it.' : 'Hover to preview. Click a dataset name to open it.'}</p>
    </div>
    <div className="siq-dataset-panel__detail research-dataset-detail" id="research-dataset-detail">
      {selected ? <>
        <div className="siq-dataset-panel__heading">
          <span><SoftwareIqNavigationIcon name={descriptor(selected.id).icon} /></span>
          <div><p>{descriptor(selected.id).eyebrow}</p><h2>{selected.label}</h2></div>
        </div>
        <p className="research-dataset-field-label" id="research-depth-label">Depth</p><div className="research-dataset-level-toggle" role="group" aria-labelledby="research-depth-label">
          {selected.levels?.length > 0 ? selected.levels.map(level => {
            const Icon = level.icon;
            const choose = () => { cancelPreview(); setLevelId(level.id); };
            return <button key={level.id} type="button" aria-pressed={selectedLevel?.id === level.id}
              onClick={choose} onMouseEnter={() => schedulePreview(choose)} onMouseLeave={cancelPreview} onFocus={() => { if (!mobile) choose(); }}>
              {Icon && <Icon aria-hidden="true" />}<span>{depthLabel(level.label)}</span>
            </button>;
          }) : <button type="button" aria-pressed="true">Market</button>}
        </div>
        <div className="research-dataset-page-panel">
          <p className="research-dataset-field-label">Pages</p>
          <div className="research-dataset-pages">{(selectedLevel?.views || selected.views || []).map(view => pageLink(selected, view))}</div>
        </div>
        <Link className="research-dataset-open" to={selectedLevel?.to || selected.defaultTo} onClick={follow}>
          Open {selectedLevel ? (selectedLevel.label === 'Vendor' ? 'company comparisons' : selectedLevel.label === 'Company Detail' ? 'company research' : selectedLevel.label.toLowerCase()) : selected.label}
        </Link>
        {DOCUMENTATION[selected.id] && <div className="research-dataset-resources">
          <Link to={DOCUMENTATION[selected.id]} onClick={follow}>Dataset documentation</Link>
        </div>}
      </> : <div className="research-dataset-empty">
        <span className="research-dataset-eyebrow">Research workspaces</span>
        <h2>Find your next research view.</h2>
        <p>Hover over a dataset or select its icon to preview pages.</p>
        
      </div>}
    </div>
  </Box>;
}
